import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getPlugin, savePluginSettings, togglePlugin } from '../../store/AsyncMethods/PluginsMethod';
import { RESET_PLUGINS_MSG } from '../../store/Types/PluginsTypes';
import backendVerifiedUser from '../../api/backendVerifiedUser';

const Field = ({ label, id, type = 'text', value, onChange, placeholder, helpText, required }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent shadow-sm"
    />
    {helpText && <p className="mt-1 text-xs text-gray-400">{helpText}</p>}
  </div>
);

const Section = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
    <h2 className="text-base font-semibold text-gray-800 border-b border-gray-100 pb-3">{title}</h2>
    {children}
  </div>
);

const Toggle = ({ label, checked, onChange, description }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
    <div>
      <p className="text-sm font-medium text-gray-700">{label}</p>
      {description && <p className="text-xs text-gray-400 mt-0.5">{description}</p>}
    </div>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-green-500' : 'bg-gray-200'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  </div>
);

export default function GreenApiPlugin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentPlugin, loading, success, error } = useSelector((s) => s.PluginsReducer);

  const [instanceId, setInstanceId] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [adminNumber, setAdminNumber] = useState('');
  const [notifyOrderPlaced, setNotifyOrderPlaced] = useState(true);
  const [notifyOrderStatus, setNotifyOrderStatus] = useState(true);
  const [notifyReviewRequest, setNotifyReviewRequest] = useState(true);

  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [connectionState, setConnectionState] = useState(null);

  useEffect(() => {
    dispatch(getPlugin('green_api'));
  }, []);

  useEffect(() => {
    if (currentPlugin?.plugin_key === 'green_api') {
      setInstanceId(currentPlugin.settings?.instance_id || '');
      setApiToken(currentPlugin.settings?.api_token || '');
      setAdminNumber(currentPlugin.settings?.admin_number || '');
      setNotifyOrderPlaced(currentPlugin.settings?.notify_order_placed !== false);
      setNotifyOrderStatus(currentPlugin.settings?.notify_order_status !== false);
      setNotifyReviewRequest(currentPlugin.settings?.notify_review_request !== false);
    }
  }, [currentPlugin]);

  useEffect(() => {
    if (success) { toast.success(success); dispatch({ type: RESET_PLUGINS_MSG }); }
    if (error) { toast.error(error); dispatch({ type: RESET_PLUGINS_MSG }); }
  }, [success, error]);

  const handleSave = async () => {
    if (!instanceId.trim() || !apiToken.trim()) {
      toast.error('Instance ID and API Token are required');
      return;
    }
    setSaving(true);
    await dispatch(savePluginSettings('green_api', {
      instance_id: instanceId.trim(),
      api_token: apiToken.trim(),
      admin_number: adminNumber.trim(),
      notify_order_placed: notifyOrderPlaced,
      notify_order_status: notifyOrderStatus,
      notify_review_request: notifyReviewRequest,
    }));
    setSaving(false);
  };

  const handleToggle = async () => {
    if (!currentPlugin?.is_active && (!instanceId.trim() || !apiToken.trim())) {
      toast.error('Please save Instance ID and API Token before activating');
      return;
    }
    setToggling(true);
    await dispatch(togglePlugin('green_api'));
    setToggling(false);
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setConnectionState(null);
    try {
      const res = await backendVerifiedUser.post('/plugins/green-api/test');
      setConnectionState(res.data.state);
      toast.success('Connection test successful!');
    } catch (err) {
      toast.error(err.response?.data?.error?.msg || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSendTest = async () => {
    if (!testPhone.trim()) { toast.error('Enter a phone number'); return; }
    setSendingTest(true);
    try {
      await backendVerifiedUser.post('/plugins/green-api/send-test', { phone: testPhone.trim() });
      toast.success('Test message sent! Check WhatsApp.');
    } catch (err) {
      toast.error(err.response?.data?.error?.msg || 'Failed to send test message');
    } finally {
      setSendingTest(false);
    }
  };

  const isActive = currentPlugin?.is_active;

  return (
    <div className="mx-3 pb-8 max-w-2xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/plugins')} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
              <path d="M11.928 0C5.352 0 0 5.379 0 12c0 2.107.549 4.082 1.51 5.799L.057 23.7l6.062-1.592A11.904 11.904 0 0011.928 24c6.576 0 11.928-5.379 11.928-12S18.504 0 11.928 0zm0 21.818a9.743 9.743 0 01-4.984-1.367l-.357-.212-3.603.946.963-3.519-.232-.371A9.754 9.754 0 012.182 12c0-5.375 4.383-9.745 9.746-9.745S21.673 6.625 21.673 12s-4.383 9.818-9.745 9.818z"/>
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Green API (WhatsApp)</h1>
            <p className="text-sm text-gray-500">Send WhatsApp messages to customers & admin</p>
          </div>
        </div>
        <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
          {isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div className="space-y-5">
        {/* Credentials */}
        <Section title="API Credentials">
          <Field
            label="Instance ID"
            id="instance_id"
            value={instanceId}
            onChange={(e) => setInstanceId(e.target.value)}
            placeholder="e.g. 1101234567"
            helpText="Found in your Green API dashboard under Instance settings"
            required
          />
          <Field
            label="API Token"
            id="api_token"
            type="password"
            value={apiToken}
            onChange={(e) => setApiToken(e.target.value)}
            placeholder="Your Green API token"
            required
          />
          <Field
            label="Admin WhatsApp Number"
            id="admin_number"
            value={adminNumber}
            onChange={(e) => setAdminNumber(e.target.value)}
            placeholder="e.g. 923001234567 (with country code, no +)"
            helpText="Admin will receive new order notifications on this number"
          />
          <div className="flex gap-3 pt-1">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
            <button
              onClick={handleToggle}
              disabled={toggling}
              className={`flex-1 py-2.5 text-sm font-medium rounded-xl transition-colors disabled:opacity-50 border ${isActive ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-green-200 text-green-600 hover:bg-green-50'}`}
            >
              {toggling ? '...' : isActive ? 'Deactivate' : 'Activate'}
            </button>
          </div>
        </Section>

        {/* Notification Settings */}
        <Section title="Notification Settings">
          <Toggle
            label="Order Placed"
            description="Send WhatsApp to customer & admin when a new order is placed"
            checked={notifyOrderPlaced}
            onChange={setNotifyOrderPlaced}
          />
          <Toggle
            label="Order Status Update"
            description="Notify customer when order status changes (e.g. Shipped, Delivered)"
            checked={notifyOrderStatus}
            onChange={setNotifyOrderStatus}
          />
          <Toggle
            label="Review Request"
            description="Send review link to customer after order is delivered"
            checked={notifyReviewRequest}
            onChange={setNotifyReviewRequest}
          />
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Notification Settings'}
          </button>
        </Section>

        {/* Test Connection */}
        <Section title="Test & Verify">
          <div className="flex gap-3">
            <button
              onClick={handleTestConnection}
              disabled={testing}
              className="flex-1 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {testing ? 'Checking...' : 'Check Connection'}
            </button>
          </div>
          {connectionState && (
            <div className={`p-3 rounded-xl text-sm font-medium ${connectionState.stateInstance === 'authorized' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
              WhatsApp Status: <strong>{connectionState.stateInstance || 'Unknown'}</strong>
              {connectionState.stateInstance !== 'authorized' && (
                <p className="text-xs mt-1 font-normal">Please scan QR code in your Green API dashboard to authorize WhatsApp.</p>
              )}
            </div>
          )}

          <div className="pt-2 border-t border-gray-100">
            <p className="text-sm font-medium text-gray-700 mb-2">Send Test Message</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="923001234567 (with country code, no +)"
                className="flex-1 px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <button
                onClick={handleSendTest}
                disabled={sendingTest || !isActive}
                className="px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50"
                title={!isActive ? 'Activate plugin first' : ''}
              >
                {sendingTest ? '...' : 'Send'}
              </button>
            </div>
            {!isActive && <p className="text-xs text-gray-400 mt-1">Activate the plugin first to send test messages.</p>}
          </div>
        </Section>

        {/* Setup Guide */}
        <Section title="Setup Guide">
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
            <li>Go to <strong>console.green-api.com</strong> and create a free account</li>
            <li>Create a new instance and copy the <strong>Instance ID</strong> and <strong>API Token</strong></li>
            <li>In the Green API dashboard, scan the QR code with your WhatsApp to authorize</li>
            <li>Paste the credentials above and click <strong>Save Settings</strong></li>
            <li>Click <strong>Check Connection</strong> — status should show <strong>authorized</strong></li>
            <li>Click <strong>Activate</strong> to enable WhatsApp notifications</li>
          </ol>
        </Section>
      </div>
    </div>
  );
}
